import { DeliveryTimeSlot, type CountryCode } from '@constants'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { ReceiptType } from '@hooks-api/types/dataModel/eCommercePayments'
import { setStorageItem, STORAGE_EVENT } from '@utils/storageHelper'
import { useEffect, useRef, useState, type SetStateAction } from 'react'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { useShallow } from 'zustand/react/shallow'
import { CarrierValidationStatus } from '../core/carrier-validation-status'
import { type WpCartValues } from '../types'

export const WO_CART_STORAGE_KEY = 'wpCartStorage'
/**
 * 登出時，直接重置 storage 防止觸發 calculate 計算
 */
export const resetWpCartStorage = () => {
  setStorageItem(WO_CART_STORAGE_KEY, {}, STORAGE_EVENT.localStorage)
}

type WpCartState = Partial<Record<CountryCode, WpCartValues>>

export const defaultCartValues: WpCartValues = {
  cart: {
    items: [],
    addonItems: [],
    specificAddonItems: [],
  },
  rewardPoints: {
    userInputRewardPoints: 0,
    isUserAppliedRewardPoints: false,
  },
  coupon: {
    manualInputCouponIds: [],
    /**
     * 常態滿額禮
     */
    selectedRegularGiveaways: [],
    /**
     * 贈品
     */
    selectedGiveaways: [],
    redeemedCodes: [],
    selectedClaimCouponRedeemCode: '',
    selectedClaimCouponId: null,
    selectedExchangeVoucherRedeemCodes: [],
    selectedExchangeVoucherGiveaways: [],
    selectedManualCouponId: null,
    hasApplyManuallyRedeemedCodes: false,
  },
  billing: {
    billingCountry: null,
    billingAddressFirstName: '',
    billingAddressPhone: '',
    billingAddressPhoneCountry: null,
    billingAddressEmail: '',
    billingAddressPostcode: '',
    billingAddressState: '',
    billingAddressCity: '',
    billingAddressAddress1: '',
    billingAddressAddress2: '',
    customerNote: '',
  },
  shipping: {
    appliedShippingMethodId: null,
    deliveryDate: '',
    deliveryTimeSlot: DeliveryTimeSlot.MORNING,
  },
  payment: {
    appliedPaymentMethodId: null,
    appliedPaymentMethodType: null,
    appliedInstallment: null,
    rememberPaymentCardToken: false,
    bankCode: null,
    userPaymentTokenInfo: null,
    cardSource: null,
  },
  invoice: {
    refundStatement: true,
    receiptType: ReceiptType.NON_BUSINESS_EINVOICE,
    businessUbnTitle: '',
    businessUbn: '',
    phoneCarrierNumber: '',
    carrierValidationStatus: CarrierValidationStatus.EMPTY,
    rememberInvoiceCarrier: false,
    charityOrganization: '',
  },
  paymentTransaction: {
    id: null,
    status: null,
    appliedRepaymentMethodId: null,
    orderHash: null,
  },
}

const migrateMap = {
  /**
   * release 2601 新增欄位 specificAddonItems
   * 單品加價購 https://www.notion.so/shepherdtech/2513-2601-2a0a4b7afc8880d983e9e0f43b98a225?source=copy_link
   */
  2601: (persistedState: WpCartState) => {
    return Object.keys(persistedState).reduce<WpCartState>((acc, countryCode) => {
      acc[countryCode as CountryCode] = {
        ...(persistedState[countryCode as CountryCode] || defaultCartValues),
        cart: {
          ...(persistedState[countryCode as CountryCode]?.cart || defaultCartValues.cart),
          specificAddonItems: [],
        },
      }
      return acc
    }, {})
  },
  /**
   * release 2607 新增欄位 selectedExchangeVoucherRedeemCodes（點數兌換券多選）
   * 舊持久化狀態缺此欄位會導致 calculate 展開時報錯，補預設空陣列。
   */
  2607: (persistedState: WpCartState) => {
    return Object.keys(persistedState).reduce<WpCartState>((acc, countryCode) => {
      const prev = persistedState[countryCode as CountryCode]
      acc[countryCode as CountryCode] = {
        ...(prev || defaultCartValues),
        coupon: {
          ...(prev?.coupon || defaultCartValues.coupon),
          selectedExchangeVoucherRedeemCodes: prev?.coupon?.selectedExchangeVoucherRedeemCodes ?? [],
        },
      }
      return acc
    }, {})
  },
  /**
   * release 2608 新增欄位 invoice.carrierValidationStatus（手機條碼載具即時驗證狀態）
   * 舊持久化狀態缺此欄位會導致 PhoneCarrierField render 期解構 carrierStatusDisplayMap[undefined] 報錯，補預設 EMPTY。
   */
  2608: (persistedState: WpCartState) => {
    return Object.keys(persistedState).reduce<WpCartState>((acc, countryCode) => {
      const prev = persistedState[countryCode as CountryCode]
      acc[countryCode as CountryCode] = {
        ...(prev || defaultCartValues),
        invoice: {
          ...(prev?.invoice || defaultCartValues.invoice),
          carrierValidationStatus: prev?.invoice?.carrierValidationStatus ?? CarrierValidationStatus.EMPTY,
        },
      }
      return acc
    }, {})
  },
  /**
   * SD-22818 hotfix：SD-21704 新增 coupon.selectedExchangeVoucherGiveaways 時漏補 migrate，
   * 舊持久化狀態缺此欄位會導致 getAllSelectedGiveawayIds flatMap 報錯、購物車頁白屏，補預設空陣列。
   * 獨立成新 version（而非併入已出版的 version 3），避免已跑過 version 3 migrate 的使用者被永久跳過。
   */
  22818: (persistedState: WpCartState) => {
    return Object.keys(persistedState).reduce<WpCartState>((acc, countryCode) => {
      const prev = persistedState[countryCode as CountryCode]
      acc[countryCode as CountryCode] = {
        ...(prev || defaultCartValues),
        coupon: {
          ...(prev?.coupon || defaultCartValues.coupon),
          selectedExchangeVoucherGiveaways: prev?.coupon?.selectedExchangeVoucherGiveaways ?? [],
        },
      }
      return acc
    }, {})
  },
  /**
   * SD-24711：清除舊 localStorage 殘留的 paymentInFlightAt 欄位。
   * 中間 build 可能在 partialize 生效前將此欄位寫入 storage；移除型別後不再讀取，但仍須主動刪除。
   */
  24711: (persistedState: WpCartState) => {
    return Object.keys(persistedState).reduce<WpCartState>((acc, countryCode) => {
      const prev = persistedState[countryCode as CountryCode]
      if (!prev) return acc
      const next = { ...prev, paymentTransaction: { ...prev.paymentTransaction } } as WpCartValues & { paymentTransaction: Record<string, unknown> }
      delete next.paymentTransaction.paymentInFlightAt
      acc[countryCode as CountryCode] = next as WpCartValues
      return acc
    }, {})
  },
  /**
   * SD-25462：清除舊 localStorage 殘留的 rewardPoints.userInputReferralCode /
   * isUserAppliedReferralCode。好友推薦碼前台 UI 移除後已無讀寫端，移除型別後不再讀取，
   * 但仍須主動刪除。
   */
  25462: (persistedState: WpCartState) => {
    return Object.keys(persistedState).reduce<WpCartState>((acc, countryCode) => {
      const prev = persistedState[countryCode as CountryCode]
      if (!prev) return acc
      const next = { ...prev, rewardPoints: { ...prev.rewardPoints } } as WpCartValues & { rewardPoints: Record<string, unknown> }
      delete next.rewardPoints.userInputReferralCode
      delete next.rewardPoints.isUserAppliedReferralCode
      acc[countryCode as CountryCode] = next as WpCartValues
      return acc
    }, {})
  },
}

const useWpCartStoreBase = create<WpCartState>()(
  persist(
    () => ({}),
    {
      name: WO_CART_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      /**
       * feature: 2601 單品加價購
       * - version 1: 新增 cart.specificAddonItems
       * - version 2: 新增 coupon.selectedExchangeVoucherRedeemCodes
       * - version 3: 新增 invoice.carrierValidationStatus
       * - version 4: SD-22818 hotfix，新增 coupon.selectedExchangeVoucherGiveaways
       * - version 5: SD-23173，version 3 撞號修補——SD-22107（補 carrierValidationStatus）與 SD-22818
       *   hotfix 線都用 version 3 但補不同欄位，導致停在 version 3/4 的使用者漏補 carrierValidationStatus、
       *   進 checkout 選手機條碼即白屏。重跑 migrateMap[2608] 對 version<5 補齊此欄位。
       * - version 6: SD-24711，移除 paymentInFlightAt（冗餘狀態，isPaymentProcessing 已全程覆蓋）
       * - version 7: SD-25462，移除 rewardPoints.userInputReferralCode /
       *   isUserAppliedReferralCode（好友推薦碼前台 UI 移除，已無讀寫端）
       */
      version: 7,
      migrate: (persistedState, version) => {
        let state = persistedState as WpCartState
        if (version < 1) {
          state = migrateMap[2601](state)
        }
        if (version < 2) {
          state = migrateMap[2607](state)
        }
        if (version < 3) {
          state = migrateMap[2608](state)
        }
        if (version < 4) {
          state = migrateMap[22818](state)
        }
        if (version < 5) {
          state = migrateMap[2608](state)
        }
        if (version < 6) {
          state = migrateMap[24711](state)
        }
        if (version < 7) {
          state = migrateMap[25462](state)
        }
        return state
      },
    },
  ),
)

/**
 * 只在 storage 內容真的被改寫時才重新 hydrate store：
 * - `storage`：其他分頁改了購物車
 * - `STORAGE_EVENT.localStorage`：同分頁透過 storageHelper 直接改寫（如登出時 resetWpCartStorage）
 * - `pageshow`（bfcache 還原）：凍結期間錯過的 storage 事件不會補送
 *
 * 不在 focus / visibilitychange 時 rehydrate：每次都整包 JSON.parse 並替換 state，
 * 會讓所有購物車訂閱者重新 render，RUM 量到 cart / checkout 點擊因此卡頓（INP）。
 */
const registerUpdateStore = () => {
  if (typeof window === 'undefined') return
  const handleStorageChange = (event: Event) => {
    if (!(event instanceof StorageEvent)) return
    if (event.storageArea && event.storageArea !== window.localStorage) return
    // key 為 null 代表 storage 被整個 clear
    if (event.key !== null && event.key !== WO_CART_STORAGE_KEY) return
    useWpCartStoreBase.persist.rehydrate()
  }
  const handlePageShow = (event: PageTransitionEvent) => {
    if (!event.persisted) return
    useWpCartStoreBase.persist.rehydrate()
  }
  window.addEventListener('storage', handleStorageChange)
  window.addEventListener(STORAGE_EVENT.localStorage, handleStorageChange)
  window.addEventListener('pageshow', handlePageShow)
}
registerUpdateStore()

export const getWpCartValues = (countryCode: CountryCode) => useWpCartStoreBase.getState()[countryCode]

export type SetWpCartValuesCallback = (state: WpCartValues) => void

/**
 * 只取 setter、不訂閱購物車內容。
 * 只需要寫入的 service hooks 請用這個而非 useWpCartStore：後者會訂閱整個國家的 slice，
 * 任何寫入都讓呼叫端重新 render；service hooks 層層巢狀、單頁有數十個實例，
 * RUM 量到 cart / checkout 點擊因此卡頓（INP）。
 *
 * callback 與 useWpCartStore 相同：在 store 更新 commit 後的 effect 執行，
 * 讓 callback 內透過 useCallbackEvent 讀到的已是更新後 render 的值。
 */
export const useWpCartSetter = (countryCode: CountryCode) => {
  const callbacks = useRef<SetWpCartValuesCallback[]>([])
  const [callbackTick, setCallbackTick] = useState(0)

  const setState = useCallbackEvent((value: SetStateAction<WpCartValues>, callback?: SetWpCartValuesCallback) => {
    useWpCartStoreBase.setState((prevState) => {
      const nextState = typeof value === 'function' ? value(prevState[countryCode] || defaultCartValues) : value
      return {
        ...prevState,
        [countryCode]: nextState,
      }
    })
    if (callback) {
      callbacks.current.push(callback)
      // 不訂閱 store，改以本地 state 觸發一次 render 來執行 callback
      setCallbackTick(tick => tick + 1)
    }
  })

  useEffect(() => {
    if (callbacks.current.length === 0) return
    const pendingCallbacks = callbacks.current
    callbacks.current = []
    const cartValues = getWpCartValues(countryCode) || defaultCartValues
    pendingCallbacks.forEach(func => func(cartValues))
  }, [callbackTick, countryCode])

  return setState
}

export const useWpCartStore = (countryCode: CountryCode) => {
  const cartValues = useWpCartStoreBase(state => state[countryCode] || defaultCartValues)
  const setState = useWpCartSetter(countryCode)

  return [cartValues, setState] as const
}

export const useWpCartSelector = <U>(countryCode: CountryCode, selector: (state: WpCartValues) => U) => {
  return useWpCartStoreBase(useShallow(state => selector(state[countryCode] || defaultCartValues)))
}

/**
 * 取得所有購物車商品（一般商品 + addon_v2 商品）
 */
export const useWpCartAllItems = (countryCode: CountryCode) => {
  return useWpCartSelector(countryCode, state => [
    ...state.cart.items,
    ...(state.cart.addonItems || []),
  ])
}

/**
 * 取得所有購物車單品加價購商品
 */
export const useWpCartAllSpecificAddonItems = (countryCode: CountryCode) => {
  return useWpCartSelector(countryCode, state => [
    ...state.cart.specificAddonItems || [],
  ])
}
